import { CdkDragDrop, DragDropModule, moveItemInArray } from '@angular/cdk/drag-drop';
import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  OnDestroy,
  OnInit,
  signal,
} from '@angular/core';
import { MatButton } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatIcon } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';
import { RouterLink } from '@angular/router';
import { TranslatePipe } from '@ngx-translate/core';
import { take } from 'rxjs';
import { DashboardCard } from '../../class/Preference';
import { TabPermission } from '../../class/TabPermission';
import { LANDMARK_CREATE } from '../../component/management-tab/landmarks-sub-tab/landmarks-routing.constants';
import { AccountStateService } from '../../services/account-state.service';
import { ApiService } from '../../services/api.service';
import { LocalStorageService } from '../../services/local-storage.service';
import { PermissionService } from '../../services/permission.service';
import { PreferenceService } from '../../services/preference.service';
import { RoutingService } from '../../services/routing.service';
import { TripstatsStoreService } from '../../services/tripstats-store.service';
import { TripStatsService } from '../../services/tripstats.service';
import { ContainerComponent } from '../layout/container/container.component';
import { LayoutComponent } from '../layout/layout.component';
import { LeftPanelComponent } from '../layout/left-panel/left-panel.component';
import { SubNavigationItemComponent } from '../layout/sub-navigation/sub-navigation-item/sub-navigation-item.component';
import { SubNavigationComponent } from '../layout/sub-navigation/sub-navigation.component';
import { TopBarComponent } from '../layout/top-bar/top-bar.component';
import { BUILD_CREATE } from '../maintenances-tab/maintenances-routing.constants';
import { ALARM_CREATE } from '../management-tab/alarms-sub-tab/alarms-routing.constants';
import { MISSION_CREATE } from '../mobility-tab/missions-sub-tab/missions-routing.constants';
import { WidgetPoiComponent } from '../shared/widget/widget-poi/widget-poi.component';
import { ChartComponent } from './chart/chart.component';
import { WidgetAlarmLogComponent } from './widget-alarm-log/widget-alarm-log.component';
import { WidgetDiagnosticComponent } from './widget-diagnostic/widget-diagnostic.component';
import { WidgetImmobilizedDeviceComponent } from './widget-immobilized-device/widget-immobilized-device.component';
import { WidgetLockedDeviceComponent } from './widget-locked-device/widget-locked-device.component';
import { WidgetMaintenanceComponent } from './widget-maintenance/widget-maintenance.component';
import { WidgetMapComponent } from './widget-map/widget-map.component';
import { WidgetNewsComponent } from './widget-news/widget-news.component';
import { WidgetPrivateComponent } from './widget-private/widget-private.component';
import { WidgetStateComponent } from './widget-state/widget-state.component';
import { WidgetValidatePoiComponent } from './widget-validate-poi/widget-validate-poi.component';

/** Widget types the dashboard knows how to render; chart cards have no `card` key. */
export const DASHBOARD_CARD_TYPES = [
  'state',
  'map',
  'news',
  'poi',
  'poitovalidate',
  'lockeddevices',
  'private',
  'maintenance-state',
  'maintenance',
  'tripless',
  'diagnostic',
  'alarm-logs',
] as const;
export type DashboardCardType = (typeof DASHBOARD_CARD_TYPES)[number];

const KNOWN_CARD_TYPES = new Set<string>(DASHBOARD_CARD_TYPES);
const NEW_CARD_CHOICES: (DashboardCardType | 'graphic')[] = [...DASHBOARD_CARD_TYPES, 'graphic'];

@Component({
  selector: 'dashboard',
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    LayoutComponent,
    LeftPanelComponent,
    ContainerComponent,
    SubNavigationComponent,
    SubNavigationItemComponent,
    TopBarComponent,
    MatIcon,
    MatMenuModule,
    TranslatePipe,
    MatCardModule,
    DragDropModule,
    MatTooltipModule,
    RouterLink,
    WidgetStateComponent,
    WidgetValidatePoiComponent,
    WidgetPrivateComponent,
    WidgetNewsComponent,
    WidgetMapComponent,
    WidgetMaintenanceComponent,
    WidgetLockedDeviceComponent,
    WidgetImmobilizedDeviceComponent,
    WidgetDiagnosticComponent,
    WidgetAlarmLogComponent,
    ChartComponent,
    WidgetPoiComponent,
    MatButton,
  ],
})
export class DashboardComponent implements OnInit, OnDestroy, AfterViewInit {
  public routingService = inject(RoutingService);
  private _accountStateService = inject(AccountStateService);
  public $account = this._accountStateService.$account;
  public tripStatsService = inject(TripStatsService);
  public tripstatsStore = inject(TripstatsStoreService);
  public preferenceService = inject(PreferenceService);
  public apiService = inject(ApiService);
  private _permissionService = inject(PermissionService);

  public changingService = false;
  public openIndexChoices = 20;
  public newCardOpen = false;
  public $isFullscreenChart = this.tripStatsService.$isFullscreen;
  public $config = this.tripStatsService.$config;

  public quickActions: {
    key: Uppercase<keyof TabPermission>;
    path: string;
    condition?: boolean;
  }[] = [];

  public $initCharts = signal<boolean>(false);
  public $isEditDash = signal<boolean>(false);
  public $isRenault = computed(() => this._accountStateService.$isRenault());
  private needSavePref = false;

  /**
   * Cards to render, skipping unknown widget types (stale or foreign preferences) that would
   * otherwise show up as empty cards, and chart cards on Renault accounts, which have no charts.
   * `index` is the position in the full preference array, which destroy/drop and the chart
   * tripstats store rely on.
   */
  public $displayableCards = computed(() =>
    (this.preferenceService.$preference()?.dashboard ?? [])
      .map((card, index) => ({ card, index }))
      .filter(({ card }) => (card?.card ? KNOWN_CARD_TYPES.has(card.card) : !this.$isRenault()))
  );

  /**
   * Choices of the "add card" menu, derived from the cards already on the dashboard: the map can
   * only be added once, and Renault accounts have no charts.
   */
  public $newCardChoices = computed(() => {
    const hasMapCard = (this.preferenceService.$preference()?.dashboard ?? []).some(
      card => card?.card === 'map'
    );
    return NEW_CARD_CHOICES.filter(choice => {
      if (choice === 'map') return !hasMapCard;
      if (choice === 'graphic') return !this.$isRenault();
      return true;
    });
  });

  ngOnInit(): void {
    this.preferenceService.preferencesLoaded$.pipe(take(1)).subscribe(() => this.initComponent());
  }

  private _initQuickActions(): DashboardComponent['quickActions'] {
    return [
      {
        key: 'POI',
        path: LANDMARK_CREATE,
        condition: this._permissionService.tabPermissionList.poi.write,
      },
      {
        key: 'MISSION',
        path: MISSION_CREATE,
        condition: this.$account().mission_access && this._permissionService.tabPermissionList.mission.write,
      },
      {
        key: 'ALARM',
        path: ALARM_CREATE,
        condition: this._permissionService.tabPermissionList.alarm.write,
      },
      {
        key: 'MAINTENANCE',
        path: BUILD_CREATE,
        condition:
          this.$account().maintenance_option && this._permissionService.tabPermissionList.maintenance.write,
      },
    ];
  }

  private initComponent(): void {
    this.quickActions = this._initQuickActions().filter(action => action.condition);
    const item = this.preferenceService.$preference()?.dashboard.find(d => d.card === 'notification');
    if (item) {
      this.preferenceService.mutatePreference(preference => {
        preference.dashboard.splice(preference.dashboard.indexOf(item), 1);
      });
      LocalStorageService.removeElement('tripstats');
      this.tripstatsStore.$tripstats.set({});
    }
  }

  ngAfterViewInit() {
    this.$initCharts.set(true);
  }

  ngOnDestroy(): void {
    if (this.needSavePref) this.preferenceService.saveDraggedPreferences('dashboard', true);
    this.tripStatsService.$isFullscreen.set(false);
    this.tripStatsService.$isEditChart.set(false);
    this.tripStatsService.index = undefined;
    this.tripStatsService.$config.set(undefined);
    this.preferenceService.componentDestroy$.next();
    this.preferenceService.componentDestroy$.complete();
  }

  public resetOptions(): void {
    this.preferenceService.resetDashboardPreference();
    this.$isEditDash.set(false);
  }

  private addGraph(): void {
    const graph = {
      y: 'stops',
      group: 'device',
      period: 'M3',
      uid_service: undefined,
    } as DashboardCard;
    this.preferenceService.mutatePreference(preference => {
      preference.dashboard.push(graph);
    });
    this.tripStatsService.resizeChart(
      true,
      this.preferenceService.$preference().dashboard?.length - 1,
      graph
    );
    this.tripStatsService.$isEditChart.set(true);
    this.toggleEdit();
  }

  public addCard(value: string): void {
    this.$isEditDash.set(true);
    if (value === 'graphic') {
      this.addGraph();
    } else {
      if (value !== null) {
        this.preferenceService.mutatePreference(preference => {
          preference.dashboard.push({ card: value });
        });
      } else {
        console.log('No card selected');
      }
    }
    this.needSavePref = true;
  }

  public destroy(index: number): void {
    this.preferenceService.mutatePreference(preference => {
      preference.dashboard.splice(index, 1);
    });
    this.needSavePref = true;
  }

  public drop(event: CdkDragDrop<number>): void {
    if (event.previousContainer.data !== event.container.data) {
      this.preferenceService.mutatePreference(preference => {
        moveItemInArray(preference.dashboard, event.previousContainer.data, event.container.data);
      });
      this.needSavePref = true;
    }
  }

  public toggleEdit() {
    this.$isEditDash.set(!this.$isEditDash());
  }
}
